import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme
} from '@mui/material';
import {
  Mic as MicIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  GraphicEq as GraphicEqIcon,
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import ScoreGradientBar from './ScoreGradientBar';

function DeviceCard({ mic: device, isLink = true, displayMode = 'circular', isLive = false }) {
  const theme = useTheme();

  // Handle case where device might be undefined or missing required fields
  if (!device) {
    return (
      <Card sx={{ height: '100%', backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Loading device...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Handle multiple events and scores
  const events = device.events || [];
  const scores = device.scores || [];

  // Use original events and scores (extra event functionality removed but structure preserved)
  const extendedEvents = events;
  const extendedScores = scores;

  // Function to format timestamp in Swedish format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { date: 'No date', time: 'No time' };
    try {
      const date = new Date(timestamp);
      const dateStr = date.toLocaleDateString('sv-SE');
      const timeStr = date.toLocaleTimeString('sv-SE', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return { date: dateStr, time: timeStr };
    } catch (error) {
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  const timestamp = formatTimestamp(device.timestamp);

  // Function to get gradient colors for circular progress
  const getProgressColor = (score) => {
    const normalizedScore = Math.min(Math.max(parseFloat(score) || 0, 0), 1);

    if (normalizedScore < 0.2) {
      return '#ff4757'; // Very Low - Dark red
    } else if (normalizedScore < 0.4) {
      return '#ff6b6b'; // Low - Red
    } else if (normalizedScore < 0.6) {
      return '#ffa502'; // Medium - Orange
    } else if (normalizedScore < 0.8) {
      return '#ffd93d'; // High - Yellow
    } else {
      return '#6bcf7f'; // Very High - Green
    }
  };

  // Calculate average score for the circular progress
  const averageScore = scores.length > 0 ? scores.reduce((sum, score) => sum + parseFloat(score), 0) / scores.length : 0;
  const progressColor = getProgressColor(averageScore);

    const cardContent = (
          <Card
        sx={{
          height: '300px', // Reduced height
          width: '100%',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
          }
        }}
      >
      <CardContent sx={{ p: 2 }}> {/* Reduced padding back to original */}
        <Box
          display="flex"
          alignItems="center"
          mb={2} // Back to original
          pb={1} // Back to original
          borderBottom={1}
          borderColor={theme.palette.divider}
        >
          <MicIcon color="primary" sx={{ fontSize: 32, mr: 1.5 }} /> {/* Slightly larger than original */}
          <Typography
            variant="h5" // Back to original
            component="h2"
            fontWeight="bold"
            sx={{ color: theme.palette.text.primary }}
          >
            {device.name}
          </Typography>
          {isLive && (
            <Chip
              label="LIVE"
              size="small"
              color="success"
              sx={{ ml: 'auto', fontSize: '0.7rem' }}
            />
          )}
        </Box>

        <Box mb={2}> {/* Back to original */}
          <Box display="flex" alignItems="center" mb={1}> {/* Back to original */}
            <StorageIcon color="action" sx={{ fontSize: 22, mr: 1.5 }} /> {/* Slightly larger than original */}
            <Typography
              variant="body1" // Back to original
              sx={{ mr: 1.5, color: theme.palette.text.secondary }}
            >
              Events & Scores:
            </Typography>
          </Box>
                      {extendedEvents.length > 0 && extendedScores.length > 0 ? (
              <Box>
                {(() => {
                  const rows = [];
                  for (let i = 0; i < extendedEvents.length; i += 2) {
                    const rowEvents = extendedEvents.slice(i, i + 2);
                    const rowScores = extendedScores.slice(i, i + 2);
                    rows.push(
                      <Box key={i} sx={{ display: 'flex', gap: 0, mb: i + 2 < extendedEvents.length ? 1 : 0 }}>
                        {rowEvents.map((event, eventIndex) => (
                          <Box
                            key={eventIndex}
                            sx={{ width: '50%' }}
                          >
                            <ScoreGradientBar
                              score={rowScores[eventIndex]}
                              event={event}
                              height={32} // Slightly larger than original 28
                              width="100%"
                              displayMode={displayMode}
                            />
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                  return rows;
                })()}
              </Box>
          ) : (
            <Box sx={{ pl: 3 }}> {/* Back to original */}
              <Typography
                variant="body1" // Back to original
                sx={{ color: theme.palette.text.secondary }}
              >
                No events data
              </Typography>
            </Box>
          )}
        </Box>

        <Box mb={1}> {/* Back to original */}
          <Box display="flex" alignItems="center" mb={0.5}> {/* Back to original */}
            <CalendarIcon color="action" sx={{ fontSize: 22, mr: 1.5 }} /> {/* Slightly larger than original */}
            <Typography
              variant="body1" // Back to original
              sx={{ mr: 1.5, color: theme.palette.text.secondary }}
            >
              Date:
            </Typography>
            <Typography
              variant="body1" // Back to original
              fontWeight="medium"
              sx={{ color: theme.palette.text.primary }}
            >
              {timestamp.date}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <ClockIcon color="action" sx={{ fontSize: 22, mr: 1.5 }} /> {/* Slightly larger than original */}
            <Typography
              variant="body1" // Back to original
              sx={{ mr: 1.5, color: theme.palette.text.secondary }}
            >
              Time:
            </Typography>
            <Typography
              variant="body1" // Back to original
              fontWeight="medium"
              sx={{ color: theme.palette.text.primary }}
            >
              {timestamp.time}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center">
          <LocationIcon color="action" sx={{ fontSize: 22, mr: 1.5 }} /> {/* Slightly larger than original */}
          <Typography
            variant="body1" // Back to original
            sx={{ mr: 1.5, color: theme.palette.text.secondary }}
          >
            Location:
          </Typography>
          <Typography
            variant="body1" // Back to original
            fontWeight="medium"
            sx={{ color: theme.palette.text.primary }}
          >
            {device.location ? device.location.replace(/,/g, ', ') : 'No location'}
          </Typography>
        </Box>
              </CardContent>
      </Card>
  );

  if (isLink) {
    return (
      <Link
        to={`/devices/${encodeURIComponent(device.name)}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

export default DeviceCard;